import { Alert, Box, Button, Grid } from "@mui/material";
import Tile from "../../../Components/Tiles/Tile";
import TileContent from "../../../Components/Tiles/TileContent";
import TextGroup from "../../VehicleSales/TextGroup";
import TextLine from "../../VehicleSales/TextLine";
import { Plate } from "../../../Components/Plate";
import formatNumber from "../../../system/formatNumber";
import formatDate from "../../../system/formatDate";
import PicturesTile, { PictureReference } from "../../../Components/PicturesTile";
import RemarketingGeneralInformationTile from "../RemarketingGeneralInformationTile";
import RemarketingAssessmentTile from "../RemarketingAssessmentTile";
import RemarketingUnregistrationTile from "../RemarketingUnregistrationTile";
import RemarketingStopList from "../RemarketingStopsTile";
import RemarketingNotesTile from "../RemarketingNotesTile";
import { AssessmentOrder, LeasmanVehicle, ReturnForm, UserRole, Vehicle, VehicleAssessment, VehicleAssessmentAttachment } from "system/Domain";
import { Dispatch, SetStateAction } from "react";
import EmailCommunicationTable from "../../../Components/EmailCommunicationTable";
import axios from "axios";
import useAction from "../../../system/useAction";
import useUser from "../../../system/useUser";
import confirm from "../../../Dialogs/confirm";
import { setUserMessage } from "../../../system/reducers/userMessage/actions";
import { useDispatch } from "react-redux";

// Macadam attaches the same photo to multiple damages (and sometimes also to the general
// attachment list), so we dedupe by storage file hash. Earlier entries in `pictures` win.
const dedupeByHash = (pictures: PictureReference[]): PictureReference[] => {
	const seenHashes = new Set<string>();
	return pictures.filter((p) => {
		if (seenHashes.has(p.picture.hash)) {
			return false;
		}
		seenHashes.add(p.picture.hash);
		return true;
	});
};

const isPicture = (a: VehicleAssessmentAttachment) =>
	a.storageFile.filename.endsWith('.jpeg') || a.storageFile.filename.endsWith('.jpg');

interface Props {
	vehicle: Vehicle
	setVehicle: Dispatch<SetStateAction<Vehicle>>
	leasmanVehicle?: LeasmanVehicle
	assessment?: VehicleAssessment
	assessmentOrder?: AssessmentOrder
	rnpPictures?: PictureReference[]
	setReturnForm: Dispatch<SetStateAction<ReturnForm>>
}

const ValidationInformation = (props: Props) => {
	const type = props.vehicle?.type;
	const emailPresent = !!props.vehicle.remarketing.leasmanContract?.customer?.email?.trim();
	const dispatch = useDispatch();
	const [, , hasRole] = useUser();

	const canResendNotification =
		emailPresent &&
		!!props.vehicle.remarketing.validation?.dateTransferred &&
		props.vehicle.remarketing.leasmanContract?.businessBranch?.isLpLegacyBusinessBranch === true &&
		(hasRole(UserRole.AldAdmin) || hasRole(UserRole.ValidationUser) || hasRole(UserRole.ValidationManager) || hasRole(UserRole.ValidationAdmin));

	const [resendNotification, isResendingNotification] = useAction(async () => {
		const result = await confirm(
			"Soll die Validierungs-E-Mail erneut an den Kunden gesendet werden?",
			"E-Mail erneut senden"
		);

		if (!result.ok) return;

		const { data } = await axios.post<Vehicle>(`/api/vehicles/${props.vehicle.id}/remarketing/validation/resend-notification`);

		props.setVehicle(data);

		dispatch(setUserMessage("E-Mail wurde erneut gesendet", "success"));
	});

	return (
		<>
			<Grid item xs={12} md={12}>
				<Grid container spacing={3}>
					<Grid item xs={4}>
						<Tile title="Informationen">
							<TileContent>
								<TextGroup title="Allgemein">
									<TextLine
										label="Kennzeichen"
										value={
											<Plate
												plateNumber={props.vehicle.plateNumber}
												style={{ zoom: 0.4 }}
											/>
										}
									/>
									<Box mt={1}>
										<TextLine label="FIN" value={props.vehicle.vin} />
										<TextLine label="Schwacke Code" value={type?.id} />
									</Box>
									{type && (
										<Box mt={1}>
											<TextLine
												label="Hersteller"
												value={type.make.name}
											/>
											<TextLine
												label="Modell"
												value={type.model.name}
											/>
											<TextLine
												label="Variante"
												value={type.model.variant}
											/>
											<TextLine
												label="Version"
												value={type.model.version}
											/>
											<TextLine
												label="Bauart"
												value={type.body.type}
											/>
											<TextLine
												label="Farbe"
												value={type.body.paint}
											/>
											<TextLine
												label="Kraftstoff"
												value={type.engine.fuelType}
											/>
											<TextLine
												label="Leistung"
												value={`${formatNumber(type.engine.power)}kW / ${formatNumber(type.engine.horsePower)}PS`}
											/>
										</Box>
									)}
								</TextGroup>
								{props.leasmanVehicle?.contract && (
									<TextGroup title="Leasingvertrag">
										<TextLine
											label="Kundennummer"
											value={props.leasmanVehicle.contract.customer.id}
										/>
										<TextLine
											label="Kunde"
											value={props.leasmanVehicle.contract.customer.fullName}
										/>
										<TextLine
											label="Email"
											value={props.leasmanVehicle.contract.customer.email}
										/>
										<TextLine
											label="Telefonnummer"
											value={props.leasmanVehicle.contract.customer.phone}
										/>
										<TextLine
											label="Vertragsnummer"
											value={props.leasmanVehicle.contract.id.toString()}
										/>
										<TextLine
											label="Vertragsbeginn"
											value={formatDate(props.leasmanVehicle.contract.begin.date)}
										/>
										<TextLine
											label="Erstzulassung"
											value={formatDate(props.vehicle.dateFirstRegistered)}
										/>
										<TextLine
											label="Vorraussichtliches Ende"
											value={formatDate(props.leasmanVehicle.contract.end.date)}
										/>
										<TextLine
											label="Rückgabedatum"
											value={formatDate(props.leasmanVehicle.contract.return.date)}
										/>
										<TextLine
											label="Rückgabe KM-Stand"
											value={formatNumber(props.leasmanVehicle.contract.return.mileage)}
										/>
										<TextLine
											label="Laufzeit"
											value={formatNumber(props.leasmanVehicle.contract.duration)}
										/>
										<TextLine
											label="Laufleistung"
											value={formatNumber(props.leasmanVehicle.contract.mileage)}
										/>
									</TextGroup>
								)}
							</TileContent>
						</Tile>
					</Grid>
					<Grid item xs={4}>
						{props.assessment && (
							<PicturesTile
								tileTitle="Gutachtenfotos"
								pictures={dedupeByHash([
									// damage pictures take priority since we need their reference later
									...(props.assessment.damages ?? []).flatMap(d => (d.attachments ?? [])
										.filter(isPicture)
										.map((ai) => ({
											title: d.text,
											picture: ai.storageFile
										}) as PictureReference)),
									...props.assessment.attachments
										.filter(isPicture)
										.map((ai) => ({
											title: ai.type,
											picture: ai.storageFile
										}) as PictureReference)
								])}
							/>
						)}
					</Grid>
					<Grid item xs={4}>
						{props.rnpPictures && props.rnpPictures.length > 0 && (
							<PicturesTile
								tileTitle="Rücknahmeprotokoll"
								pictures={props.rnpPictures}
							/>
						)}
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12}>
				<Grid container spacing={3}>
					<Grid item xs={12} md={4}>
						<RemarketingGeneralInformationTile
							vehicle={props.vehicle}
							onReturnFormLoaded={(rf) => props.setReturnForm(rf)}
						/>
					</Grid>
					<Grid item xs={12} md={4}>
						<RemarketingAssessmentTile
							vehicle={props.vehicle}
							assessment={props.assessment}
							assessmentOrder={props.assessmentOrder}
						/>
					</Grid>
					<Grid item xs={12} md={4}>
						<RemarketingUnregistrationTile vehicle={props.vehicle} />
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12}>
				<Grid container spacing={3}>
					<Grid item xs={12} md={4}>
						<RemarketingStopList
							vehicle={props.vehicle}
							setVehicle={props.setVehicle}
							excluded={["VehicleAssessmentRejection", "PictureSetQuality", "ValidationMissingParts"]}
						/>
						<RemarketingNotesTile vehicle={props.vehicle} setVehicle={props.setVehicle} />
					</Grid>
					{(props.vehicle.remarketing.validation?.emailCommunications || !emailPresent) && (
						<Grid item xs={12} md={8}>
							<Tile
								title={
									<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
										<span>Email Kommunikation</span>
										{canResendNotification && (
											<Button
												size="small"
												variant="outlined"
												disabled={isResendingNotification}
												onClick={resendNotification}
											>
												E-Mail erneut senden
											</Button>
										)}
									</Box>
								}
							>
								<TileContent dense>
									{!emailPresent && (
										<Alert severity="warning" variant="outlined">
											Die Emailadresse des Kunden ist nicht gepflegt. <br /><br />Für nachfolgende Prozesse ist diese notwendig. Bitte veranlassen Sie die Pflege in Leasman und synchronisieren sie erneut.
										</Alert>
									)}
									<EmailCommunicationTable emails={props.vehicle.remarketing.validation?.emailCommunications} />
								</TileContent>
							</Tile>
						</Grid>
					)}
					{props.vehicle.remarketing.emailCommunications?.length > 0 && (
						<Grid item xs={12} md={8}>
							<Tile title="Remarketing Email Kommunikation">
								<TileContent dense>
									<EmailCommunicationTable emails={props.vehicle.remarketing.emailCommunications} />
								</TileContent>
							</Tile>
						</Grid>
					)}
				</Grid>
			</Grid>
		</>
	);
};

export default ValidationInformation;
